import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CreateMeRequestBody, MeRequestMessageBody } from "@evolua/api-client";
import { getApiClient } from "../auth/http.js";
import { useSession } from "../session/SessionContext.js";
import {
  addDemoMeRequestMessage,
  createDemoMeRequest,
  getDemoMeRequest,
  listDemoMeRequests,
} from "./demo-me-requests.js";
import { queryKeys } from "./query-keys.js";

function invalidate(queryClient: ReturnType<typeof useQueryClient>) {
  void queryClient.invalidateQueries({ queryKey: queryKeys.me.requests() });
  void queryClient.invalidateQueries({ queryKey: queryKeys.me.inboxAll() });
}

export function useMeRequests() {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.requests(),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(listDemoMeRequests())
        : getApiClient().listMeRequests(),
  });
}

export function useMeRequest(id: string | null) {
  const { isDemoMode } = useSession();
  return useQuery({
    queryKey: queryKeys.me.request(id ?? ""),
    enabled: Boolean(id),
    queryFn: () =>
      isDemoMode
        ? Promise.resolve(getDemoMeRequest(id!))
        : getApiClient().getMeRequest(id!),
  });
}

export function useCreateMeRequest() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateMeRequestBody) =>
      isDemoMode
        ? Promise.resolve(createDemoMeRequest(body))
        : getApiClient().createMeRequest(body),
    onSuccess: () => invalidate(queryClient),
  });
}

export function useAddMeRequestMessage() {
  const { isDemoMode } = useSession();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; body: MeRequestMessageBody }) =>
      isDemoMode
        ? Promise.resolve(addDemoMeRequestMessage(input.id, input.body))
        : getApiClient().addMeRequestMessage(input.id, input.body),
    onSuccess: (_data, vars) => {
      invalidate(queryClient);
      void queryClient.invalidateQueries({
        queryKey: queryKeys.me.request(vars.id),
      });
    },
  });
}
